import { Link, useLocation } from "react-router-dom";
import { House, Search, LayoutGrid, ShoppingBag, UserCircle } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";

interface MobileBottomNavProps {
  onSearchClick: () => void;
  onCartClick: () => void;
  onAuthClick: () => void;
  onCategoriesClick: () => void;
  cartCount?: number;
  isLoggedIn?: boolean;
}

export const MobileBottomNav = ({
  onSearchClick,
  onCartClick,
  onAuthClick,
  onCategoriesClick,
  cartCount = 0,
  isLoggedIn = false,
}: MobileBottomNavProps) => {
  const location = useLocation();
  const { itemCount, isAnimating } = useCart();
  
  const isActive = (path: string) => location.pathname === path;

  // Use context itemCount if available, otherwise fallback to prop
  const displayCount = itemCount || cartCount;

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[150] bg-background/95 backdrop-blur-lg border-t border-border safe-area-bottom">
      <div className="grid grid-cols-5 h-16">
        {/* Home */}
        <Link
          to="/"
          className={`flex flex-col items-center justify-center gap-1 transition-colors ${
            isActive("/") ? "text-primary" : "text-muted-foreground"
          }`}
        >
          <House className="w-5 h-5" strokeWidth={isActive("/") ? 2.5 : 1.5} />
          <span className="text-[10px] font-medium">Domov</span>
        </Link>

        {/* Search */}
        <button
          onClick={onSearchClick}
          className="flex flex-col items-center justify-center gap-1 text-muted-foreground active:text-foreground transition-colors"
        >
          <Search className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Hľadať</span>
        </button>

        {/* Categories */}
        <button
          onClick={onCategoriesClick}
          className="flex flex-col items-center justify-center gap-1 text-muted-foreground active:text-foreground transition-colors"
        >
          <LayoutGrid className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Kategórie</span>
        </button>

        {/* Cart */}
        <button
          onClick={onCartClick}
          className="flex flex-col items-center justify-center gap-1 text-muted-foreground active:text-foreground transition-colors relative"
        >
          <div className="relative">
            <ShoppingBag className={cn("w-5 h-5 transition-transform", isAnimating && "cart-bounce")} strokeWidth={1.5} />
            {displayCount > 0 && (
              <span className={cn(
                "absolute -top-1 -right-2 min-w-[16px] h-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center px-1",
                isAnimating && "cart-badge-pop"
              )}>
                {displayCount > 9 ? "9+" : displayCount}
              </span>
            )}
          </div>
          <span className="text-[10px] font-medium">Košík</span>
        </button>

        {/* Account */}
        {isLoggedIn ? (
          <Link
            to="/profil"
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              isActive("/profil") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <UserCircle className="w-5 h-5" strokeWidth={isActive("/profil") ? 2.5 : 1.5} />
            <span className="text-[10px] font-medium">Účet</span>
          </Link>
        ) : (
          <button
            onClick={onAuthClick}
            className="flex flex-col items-center justify-center gap-1 text-muted-foreground active:text-foreground transition-colors"
          >
            <UserCircle className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium">Účet</span>
          </button>
        )}
      </div>
    </nav>
  );
};
